'use client';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, SlidersHorizontal, Star, MapPin, Languages, CheckCircle2, Clock, X, ChevronDown } from 'lucide-react';
import HomeNavbar from '@/app/home-screen/components/HomeNavbar';
import HomeBottomNav from '@/app/home-screen/components/HomeBottomNav';

export interface Priest {
  id: string;
  name: string;
  title: string;
  experience: string;
  experienceYears: number;
  languages: string[];
  tradition: string;
  veda: string;
  rating: number;
  reviewCount: number;
  ritualsCount: number;
  location: string;
  city: string;
  availability: 'Available Today' | 'Available This Week' | 'Booked';
  verified: boolean;
  image: string;
  alt: string;
  specializations: string[];
  startingFee: number;
}

export const PRIESTS_DATA: Priest[] = [
{
  id: 'raghavendra-sharma',
  name: 'Pandit Raghavendra Sharma',
  title: 'Vedic Priest & Ritual Specialist',
  experience: '28 years',
  experienceYears: 28,
  languages: ['Sanskrit', 'Kannada', 'Telugu', 'Hindi', 'English'],
  tradition: 'Smartha',
  veda: 'Yajur Veda',
  rating: 4.9,
  reviewCount: 847,
  ritualsCount: 1240,
  location: 'Jayanagar, Bengaluru',
  city: 'Bengaluru',
  availability: 'Available Today',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12902d9c2-1767781510201.png",
  alt: 'Pandit Raghavendra Sharma, experienced Vedic priest in traditional attire',
  specializations: ['Gruhapravesam', 'Vivah Sanskar', 'Rudrabhishek', 'Satyanarayan Puja'],
  startingFee: 2100
},
{
  id: 'krishnamurthy-iyer',
  name: 'Pandit Krishnamurthy Iyer',
  title: 'Agamic Priest & Temple Ritualist',
  experience: '35 years',
  experienceYears: 35,
  languages: ['Sanskrit', 'Tamil', 'Telugu', 'English'],
  tradition: 'Shaiva Agama',
  veda: 'Sama Veda',
  rating: 5.0,
  reviewCount: 1120,
  ritualsCount: 2100,
  location: 'Mylapore, Chennai',
  city: 'Chennai',
  availability: 'Available This Week',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b4572d2e-1764763519362.png",
  alt: 'Pandit Krishnamurthy Iyer, senior Shaiva Agama priest from Chennai',
  specializations: ['Rudrabhishek', 'Navagraha Shanti', 'Durga Puja', 'Havan Homa'],
  startingFee: 3500
},
{
  id: 'venkatesh-bhat',
  name: 'Pandit Venkatesh Bhat',
  title: 'Vaishnava Priest & Katha Specialist',
  experience: '18 years',
  experienceYears: 18,
  languages: ['Sanskrit', 'Kannada', 'Hindi'],
  tradition: 'Vaishnava',
  veda: 'Rig Veda',
  rating: 4.8,
  reviewCount: 562,
  ritualsCount: 780,
  location: 'Malleswaram, Bengaluru',
  city: 'Bengaluru',
  availability: 'Available Today',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_10aa9e8f1-1779109546189.png",
  alt: 'Pandit Venkatesh Bhat, Vaishnava priest specializing in Satyanarayan Katha',
  specializations: ['Satyanarayan Puja', 'Lakshmi Puja', 'Ganesh Puja', 'Namakarana'],
  startingFee: 1500
},
{
  id: 'subramaniam-pillai',
  name: 'Pandit Subramaniam Pillai',
  title: 'Tantric Priest & Shakta Ritualist',
  experience: '22 years',
  experienceYears: 22,
  languages: ['Sanskrit', 'Tamil', 'Malayalam'],
  tradition: 'Shakta Tantra',
  veda: 'Atharva Veda',
  rating: 4.7,
  reviewCount: 389,
  ritualsCount: 640,
  location: 'Thiruvananthapuram, Kerala',
  city: 'Thiruvananthapuram',
  availability: 'Available This Week',
  verified: true,
  image: "https://images.unsplash.com/photo-1575191766658-06ceb50a858e",
  alt: 'Pandit Subramaniam Pillai, Shakta Tantra priest from Kerala',
  specializations: ['Durga Puja', 'Havan Homa', 'Navagraha Shanti', 'Shraddha Karma'],
  startingFee: 2800
},
{
  id: 'ramesh-upadhyay',
  name: 'Pandit Ramesh Upadhyay',
  title: 'Vedic Astrologer & Ritual Priest',
  experience: '14 years',
  experienceYears: 14,
  languages: ['Sanskrit', 'Hindi', 'English', 'Marathi'],
  tradition: 'Smartha',
  veda: 'Yajur Veda',
  rating: 4.6,
  reviewCount: 298,
  ritualsCount: 420,
  location: 'Dadar, Mumbai',
  city: 'Mumbai',
  availability: 'Available Today',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a67f10f0-1767013801785.png",
  alt: 'Pandit Ramesh Upadhyay, Vedic astrologer and ritual priest from Mumbai',
  specializations: ['Navagraha Shanti', 'Ganesh Puja', 'Satyanarayan Puja', 'Vivah Sanskar'],
  startingFee: 1800
},
{
  id: 'ananda-swami',
  name: 'Swami Ananda Giri',
  title: 'Vedanta Scholar & Ritual Guide',
  experience: '40 years',
  experienceYears: 40,
  languages: ['Sanskrit', 'Hindi', 'English', 'Bengali'],
  tradition: 'Advaita Vedanta',
  veda: 'Rig Veda',
  rating: 4.9,
  reviewCount: 1340,
  ritualsCount: 3200,
  location: 'Rishikesh, Uttarakhand',
  city: 'Rishikesh',
  availability: 'Available This Week',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_184ba937b-1785773643427.png",
  alt: 'Swami Ananda Giri, senior Vedanta scholar and ritual guide from Rishikesh',
  specializations: ['Rudrabhishek', 'Havan Homa', 'Vivah Sanskar', 'Gruhapravesam'],
  startingFee: 5000
},
{
  id: 'narasimha-dikshit',
  name: 'Pandit Narasimha Dikshit',
  title: 'Smartha Priest & Life Rites Specialist',
  experience: '12 years',
  experienceYears: 12,
  languages: ['Sanskrit', 'Telugu', 'Kannada'],
  tradition: 'Smartha',
  veda: 'Yajur Veda',
  rating: 4.7,
  reviewCount: 214,
  ritualsCount: 310,
  location: 'Secunderabad, Hyderabad',
  city: 'Hyderabad',
  availability: 'Booked',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b200635-1785773643957.png",
  alt: 'Pandit Narasimha Dikshit, Smartha priest specializing in life rites from Hyderabad',
  specializations: ['Namakarana', 'Vidyarambha', 'Vivah Sanskar', 'Shraddha Karma'],
  startingFee: 1200
},
{
  id: 'gopinath-mishra',
  name: 'Pandit Gopinath Mishra',
  title: 'Vaishnava Priest & Bhagavata Katha',
  experience: '25 years',
  experienceYears: 25,
  languages: ['Sanskrit', 'Hindi', 'Odia', 'Bengali'],
  tradition: 'Vaishnava',
  veda: 'Sama Veda',
  rating: 4.8,
  reviewCount: 672,
  ritualsCount: 950,
  location: 'Puri, Odisha',
  city: 'Puri',
  availability: 'Available Today',
  verified: true,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0f6bc60-1785773643476.png",
  alt: 'Pandit Gopinath Mishra, Vaishnava priest and Bhagavata Katha specialist from Puri',
  specializations: ['Satyanarayan Puja', 'Lakshmi Puja', 'Durga Puja', 'Havan Homa'],
  startingFee: 2200
}];


const ALL_LANGUAGES = ['All', 'Sanskrit', 'Hindi', 'English', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Marathi', 'Odia'];
const ALL_CITIES = ['All', 'Bengaluru', 'Chennai', 'Mumbai', 'Hyderabad', 'Rishikesh', 'Thiruvananthapuram', 'Puri'];
const ALL_TRADITIONS = ['All', 'Smartha', 'Vaishnava', 'Shaiva Agama', 'Shakta Tantra', 'Advaita Vedanta'];
const EXPERIENCE_OPTIONS = ['All', '0–10 years', '10–20 years', '20–30 years', '30+ years'];
const AVAILABILITY_OPTIONS = ['All', 'Available Today', 'Available This Week'];

const availabilityColor: Record<string, string> = {
  'Available Today': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Available This Week': 'bg-amber-100 text-amber-700 border-amber-200',
  'Booked': 'bg-red-50 text-red-500 border-red-200'
};

function PriestCard({ priest }: {priest: Priest;}) {
  return (
    <Link href={`/priests/${priest.id}`} className="group block">
      <div className="bg-white rounded-2xl border border-gold/20 overflow-hidden shadow-sm hover:shadow-xl hover:border-gold/40 transition-all duration-300 hover:-translate-y-0.5">
        {/* Photo */}
        <div className="relative h-52 overflow-hidden bg-gradient-to-br from-amber-50 to-orange-50">
          <img
            src={priest.image}
            alt={priest.alt}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {(e.target as HTMLImageElement).src = '/assets/images/no_image.png';}} />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          {/* Verified badge */}
          {priest.verified &&
          <div className="absolute top-3 left-3 flex items-center gap-1 bg-white/90 backdrop-blur-sm text-emerald-700 text-[10px] font-bold px-2 py-1 rounded-full shadow-sm border border-emerald-200">
              <CheckCircle2 size={10} className="text-emerald-500" />
              Verified
            </div>
          }
          {/* Availability */}
          <div className={`absolute top-3 right-3 text-[10px] font-semibold px-2 py-1 rounded-full border ${availabilityColor[priest.availability]}`}>
            {priest.availability}
          </div>
          {/* Rating overlay */}
          <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-full">
            <Star size={10} fill="white" />
            {priest.rating} <span className="font-normal opacity-80">({priest.reviewCount})</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="mb-2">
            <h3 className="font-display text-base font-bold text-maroon leading-tight group-hover:text-saffron transition-colors">{priest.name}</h3>
            <p className="text-xs text-maroon/50 font-body mt-0.5">{priest.title}</p>
          </div>

          {/* Tradition & Experience */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className="text-[10px] bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full font-medium">{priest.tradition}</span>
            <span className="text-[10px] bg-slate-50 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Clock size={8} />
              {priest.experience}
            </span>
          </div>

          {/* Location */}
          <div className="flex items-center gap-1.5 text-xs text-maroon/50 mb-2">
            <MapPin size={11} className="text-saffron flex-shrink-0" />
            <span className="truncate">{priest.location}</span>
          </div>

          {/* Languages */}
          <div className="flex items-start gap-1.5 mb-3">
            <Languages size={11} className="text-saffron flex-shrink-0 mt-0.5" />
            <div className="flex flex-wrap gap-1">
              {priest.languages.slice(0, 3).map((lang) =>
              <span key={lang} className="text-[10px] bg-cream text-maroon/60 px-1.5 py-0.5 rounded border border-gold/20">{lang}</span>
              )}
              {priest.languages.length > 3 &&
              <span className="text-[10px] text-maroon/40">+{priest.languages.length - 3}</span>
              }
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-gold/15">
            <div>
              <span className="text-xs text-maroon/40">Starting from</span>
              <div className="text-sm font-bold text-maroon">₹{priest.startingFee.toLocaleString()}</div>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = '/booking-flow'; }}
              className="bg-saffron text-white text-xs font-semibold px-3 py-1.5 rounded-full group-hover:bg-maroon transition-colors"
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    </Link>);

}

function FilterSelect({
  label, value, options, onChange
}: {label: string;value: string;options: string[];onChange: (v: string) => void;}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none w-full bg-white border border-gold/30 text-maroon text-sm font-medium rounded-xl px-3 py-2.5 pr-8 focus:outline-none focus:border-saffron/60 focus:ring-2 focus:ring-saffron/10 cursor-pointer">
        
        {options.map((opt) =>
        <option key={opt} value={opt}>{opt === 'All' ? `${label}: All` : opt}</option>
        )}
      </select>
      <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-maroon/40 pointer-events-none" />
    </div>);

}

export default function PriestsPage() {
  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('All');
  const [city, setCity] = useState('All');
  const [tradition, setTradition] = useState('All');
  const [experience, setExperience] = useState('All');
  const [availability, setAvailability] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    return PRIESTS_DATA.filter((p) => {
      const q = search.toLowerCase();
      const matchSearch = !q || p.name.toLowerCase().includes(q) || p.tradition.toLowerCase().includes(q) || p.city.toLowerCase().includes(q) || p.specializations.some((s) => s.toLowerCase().includes(q));
      const matchLang = language === 'All' || p.languages.includes(language);
      const matchCity = city === 'All' || p.city === city;
      const matchTradition = tradition === 'All' || p.tradition === tradition;
      const matchAvail = availability === 'All' || p.availability === availability;
      const matchExp = experience === 'All' || (
      experience === '0–10 years' ? p.experienceYears < 10 :
      experience === '10–20 years' ? p.experienceYears >= 10 && p.experienceYears < 20 :
      experience === '20–30 years' ? p.experienceYears >= 20 && p.experienceYears < 30 :
      p.experienceYears >= 30);

      return matchSearch && matchLang && matchCity && matchTradition && matchAvail && matchExp;
    });
  }, [search, language, city, tradition, experience, availability]);

  const activeFilters = [language, city, tradition, experience, availability].filter((v) => v !== 'All').length;

  const clearAll = () => {
    setSearch('');
    setLanguage('All');
    setCity('All');
    setTradition('All');
    setExperience('All');
    setAvailability('All');
  };

  return (
    <div className="min-h-screen bg-ivory">
      <HomeNavbar />

      {/* Page Header */}
      <div className="pt-20 pb-10 px-4" style={{ background: 'linear-gradient(135deg, #500A0A 0%, #7B1C1C 50%, #9B3A1A 100%)' }}>
        <div className="max-w-screen-xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-amber-200 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
            <CheckCircle2 size={12} />
            All priests are background-verified
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mb-3">
            Find Your Priest
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto font-body">
            Connect with verified Vedic priests for authentic rituals. Every priest on Yagnick is personally vetted for knowledge, conduct, and experience.
          </p>

          {/* Search Bar */}
          <div className="mt-6 max-w-lg mx-auto relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-maroon/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, tradition, ritual…"
              className="w-full bg-white text-maroon placeholder-maroon/40 text-sm font-body rounded-2xl pl-10 pr-4 py-3.5 shadow-lg focus:outline-none focus:ring-2 focus:ring-saffron/30" />
            
            {search &&
            <button onClick={() => setSearch('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-maroon/40 hover:text-maroon">
                <X size={14} />
              </button>
            }
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 md:pb-12">
        {/* Filters Row */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl border transition-all ${
            filtersOpen || activeFilters > 0 ?
            'bg-saffron text-white border-saffron shadow-md' :
            'bg-white text-maroon border-gold/30 hover:border-saffron/40'}`
            }>
            
            <SlidersHorizontal size={15} />
            Filters
            {activeFilters > 0 &&
            <span className="bg-white/30 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {activeFilters}
              </span>
            }
          </button>

          {/* Results count */}
          <span className="text-sm text-maroon/50 font-body">
            {filtered.length} priest{filtered.length !== 1 ? 's' : ''} found
          </span>

          {activeFilters > 0 &&
          <button onClick={clearAll} className="flex items-center gap-1 text-xs text-saffron font-medium hover:underline ml-auto">
              <X size={12} />
              Clear all
            </button>
          }
        </div>

        {/* Filter Panel */}
        {filtersOpen &&
        <div className="bg-white border border-gold/20 rounded-2xl p-4 mb-6 shadow-sm">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <FilterSelect label="Language" value={language} options={ALL_LANGUAGES} onChange={setLanguage} />
              <FilterSelect label="City" value={city} options={ALL_CITIES} onChange={setCity} />
              <FilterSelect label="Tradition" value={tradition} options={ALL_TRADITIONS} onChange={setTradition} />
              <FilterSelect label="Experience" value={experience} options={EXPERIENCE_OPTIONS} onChange={setExperience} />
              <FilterSelect label="Availability" value={availability} options={AVAILABILITY_OPTIONS} onChange={setAvailability} />
            </div>
          </div>
        }

        {/* Priest Grid */}
        {filtered.length > 0 ?
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((priest) =>
          <PriestCard key={priest.id} priest={priest} />
          )}
          </div> :

        <div className="text-center py-20">
            <div className="text-5xl mb-4">🙏</div>
            <h3 className="font-display text-xl font-bold text-maroon mb-2">No priests found</h3>
            <p className="text-maroon/50 text-sm font-body mb-4">Try adjusting your filters or search query.</p>
            <button onClick={clearAll} className="text-saffron font-medium text-sm hover:underline">Clear all filters</button>
          </div>
        }
      </div>

      <HomeBottomNav />
    </div>);

}